/* src/Pages/SlotsInactivePage/index.tsx */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Card, Space, message, Spin } from 'antd';
import type { Key } from 'react';

import {
  fetchSlots,
  fetchAllSlots,
  deleteSlot,
} from '../../Modules/Slot/Presentation/actions';

import { Slot, SlotSearchParams } from '../../Modules/Slot/Model/Slot';
import SlotTable from './components/SlotTable';
import ManagementBar from './components/ManagementBar';
import SlotOpsOverrideBanner from '../../components/SlotOpsOverrideBanner';

import { useAppDispatch, useAppSelector } from '../../Modules/Presentation/hooks';
import { useSlots } from '../../Modules/Slot/Presentation/hooks/hooks';
import { selectUser } from '../../Modules/User/Presentation/selectors';
import { selectAccounts } from '../../Modules/Account/Presentation/selectors';
import { fetchAccounts } from '../../Modules/Account/Presentation/actions';
import useOnce from '../../Utils/hook/useOnce';
import { getAccountIdById } from '../../Utils/getAccountNameById';
import { assertAdminOrManager } from '../../Modules/Slot/Domain/UseCase/Utils/Permission';
import { api } from '../../api/axiosInstance';

const SlotsInactivePage: React.FC = () => {

  /** ---------------------------------------------------------------- */
  const dispatch = useAppDispatch();
  const me = useAppSelector(selectUser);
  const accounts = useAppSelector(selectAccounts);
  
  // 컴포넌트 최상단 ref
  const topRef = useRef<HTMLDivElement>(null);

  /* ==============  검색/필터 상태 ============== */
  const [searchParams, setSearchParams] = useState<SlotSearchParams>({
    page: 1,
    pageSize: 20,
    active: 'false', // 비활성 슬롯만 조회
    order_by: 'registered',
    order_dir: 'desc'
  });

  const [search, setSearch] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [searchField, setSearchField] = useState<'slot' | 'keyword' | 'url' | 'user' | 'mid_single' | 'mid_compare'>('slot');

  // 검색 필터 변경 시 키워드 초기화 및 기본 페이지로 리셋
  const handleSearchFieldChange = useCallback((field: 'slot' | 'keyword' | 'url' | 'user' | 'mid_single' | 'mid_compare') => {
    setSearchField(field);
    setSearch('');
    setSearchValue('');
    
    setSearchParams(prev => ({
      page: 1,
      pageSize: prev.pageSize,
      active: 'false'
    }));
  }, []);

  /* ==============  UI 상태 ============== */
  const [selected, setSelected] = useState<Key[]>([]);

  /** me 가 바뀔 때마다 슬롯 훅 */
  const { slots, loading, error, pagination, filters, refresh } = useSlots(me!, searchParams);

  // 환불-종료 슬롯 페이지에서는 캐싱 없이 항상 새로 요청
  const fetchInactiveSlots = useCallback(() => {
    if (me) {
      dispatch(fetchSlots({ me, params: searchParams }));
    }
  }, [dispatch, me, searchParams]);

  /* ➜ 최초 한 번: 계정 목록, 슬롯 목록 */
  useOnce(() => { if (accounts.length === 0) dispatch(fetchAccounts()); });
  useOnce(() => { fetchInactiveSlots(); });

  // searchParams가 변경될 때마다 새로 요청
  useEffect(() => {
    if (me) {
      fetchInactiveSlots();
    }
  }, [fetchInactiveSlots, me]);

  /* ==============  검색 파라미터 업데이트 ============== */
  const updateSearchParams = useCallback((newParams: Partial<SlotSearchParams>) => {
    setSearchParams(prev => ({
      ...prev,
      ...newParams,
      // 검색 조건이 변경될 때만 페이지를 1로 설정
      page: (newParams.slotId || newParams.keyword || newParams.url || newParams.sellerId || newParams.mid_single || newParams.mid_compare) ? 1 : prev.page
    }));
  }, []);

  // 검색어 변경 시 파라미터 업데이트
  useEffect(() => {
    const params: Partial<SlotSearchParams> = {};
    
    if (searchValue.trim()) {
      switch (searchField) {
        case 'slot':
          const slotId = parseInt(searchValue.trim());
          if (!isNaN(slotId)) {
            params.slotId = slotId;
          }
          break;
        case 'keyword':
          params.keyword = searchValue.trim();
          break;
        case 'url':
          params.url = searchValue.trim();
          break;
        case 'user': {
          const trimmed = searchValue.trim();
          const searchLower = trimmed.toLowerCase();
          const sellersOnly = accounts.filter(acc => acc.grade === 'SELLER');

          // 1) 정확 일치 (username) 우선
          const exactUsername = sellersOnly.find(acc => acc.username.toLowerCase() === searchLower);
          if (exactUsername) {
            params.sellerId = exactUsername.id;
            break;
          }

          // 2) 정확 일치 (name)
          const exactName = sellersOnly.find(acc => acc.name.toLowerCase() === searchLower);
          if (exactName) { params.sellerId = exactName.id; break; }

          // 3) 숫자인 경우 ID로 검색 (단, 완전히 숫자인 경우만)
          if (/^\d+$/.test(trimmed)) {
            params.sellerId = parseInt(trimmed);
            break;
          }

          // 4) prefix 일치 (username)
          const startsUser = sellersOnly.filter(acc => acc.username.toLowerCase().startsWith(searchLower));
          if (startsUser.length > 0) {
            startsUser.sort((a, b) => a.username.length - b.username.length || a.id - b.id);
            params.sellerId = startsUser[0].id; break;
          }

          // 5) prefix 일치 (name)
          const startsName = sellersOnly.filter(acc => acc.name.toLowerCase().startsWith(searchLower));
          if (startsName.length > 0) {
            startsName.sort((a, b) => a.name.length - b.name.length || a.id - b.id);
            params.sellerId = startsName[0].id; break;
          }

          // 6) 부분 일치 (username)
          const includesUser = sellersOnly.filter(acc => acc.username.toLowerCase().includes(searchLower));
          if (includesUser.length > 0) {
            includesUser.sort((a, b) => {
              const ia = a.username.toLowerCase().indexOf(searchLower);
              const ib = b.username.toLowerCase().indexOf(searchLower);
              if (ia !== ib) return ia - ib;
              return a.username.length - b.username.length || a.id - b.id;
            });
            params.sellerId = includesUser[0].id; break;
          }

          // 7) 부분 일치 (name)
          const includesName = sellersOnly.filter(acc => acc.name.toLowerCase().includes(searchLower));
          if (includesName.length > 0) {
            includesName.sort((a, b) => {
              const ia = a.name.toLowerCase().indexOf(searchLower);
              const ib = b.name.toLowerCase().indexOf(searchLower);
              if (ia !== ib) return ia - ib;
              return a.name.length - b.name.length || a.id - b.id;
            });
            params.sellerId = includesName[0].id; break;
          }

          break;
        }
        case 'mid_single':
          params.mid_single = searchValue.trim();
          break;
        case 'mid_compare':
          params.mid_compare = searchValue.trim();
          break;
      }
    }

    if (searchField === 'mid_single' && searchValue.trim()) {
      params.mid_single = searchValue.trim();
    }
    if (searchField === 'mid_compare' && searchValue.trim()) {
      params.mid_compare = searchValue.trim();
    }

    // 비활성 슬롯만 조회
    params.active = 'false';

    updateSearchParams(params);
  }, [searchValue, searchField, updateSearchParams, accounts]);

  // 검색 실행
  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
    
    if (!value.trim()) {
      setSearchParams(prev => ({
        page: 1,
        pageSize: prev.pageSize,
        active: 'false'
      }));
    }
  }, []);

  // 페이지 변경
  const handlePageChange = useCallback((page: number, pageSize?: number) => {
    setSearchParams(prev => ({
      ...prev,
      page,
      pageSize: pageSize || prev.pageSize
    }));
  }, []); // searchParams.pageSize 의존성 제거

  // 페이지 변경 시 화면을 위로 이동
  useEffect(() => {
    if (searchParams.page) {
      setTimeout(() => {
        topRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [searchParams.page]);

  /* ==============  선택 슬롯 정보 ============== */
  const selectedSlots = useMemo(() => 
    slots.filter(s => selected.includes(s.slotId)),
    [slots, selected]
  );

  /* ==============  공통 성공 콜백 ============== */
  const handleSuccess = useCallback((msg: string) => {
    message.success(msg);
    setSelected([]);
  }, []);

  /* ---------------- 삭제 ---------------- */
  const onDelete = useCallback(async () => {
    if (!me) return;
    try {
      assertAdminOrManager(me);
    } catch (e) {
      message.error((e as Error).message);
      return;
    }

    try {
      await Promise.all(
        (selected as number[]).map(id =>
          dispatch(deleteSlot({ id, me }))
            .unwrap()
            .then(() => true)
            .catch(() => false)
        )
      );
      
      fetchInactiveSlots();
      handleSuccess(`${selected.length}개 슬롯이 삭제되었습니다.`);
    } catch {
      message.error('슬롯 삭제에 실패했습니다.');
    }
  }, [me, selected, dispatch, handleSuccess, fetchInactiveSlots]);

  /* ---------------- 엑셀 다운로드 ---------------- */
  const [legacyExcelDownloading, setLegacyExcelDownloading] = useState(false);
  const [excelDownloading, setExcelDownloading] = useState(false);

  const handleLegacyExcelDownload = useCallback(async () => {
    if (!me) return;
    setLegacyExcelDownloading(true);
    try {
      const slotsToExport = selected.length > 0
        ? selectedSlots
        : (await dispatch(fetchAllSlots({ me, active: false })).unwrap()).slots;
      if (slotsToExport.length === 0) {
        message.warning('다운로드할 슬롯이 없습니다.');
        return;
      }
      const { exportSlotsToExcel } = await import('../../Utils/exportSlotsToExcel');
      exportSlotsToExcel(slotsToExport, accounts, selected.length === 0);
      message.success(`${slotsToExport.length}개 슬롯이 다운로드되었습니다.`);
    } catch {
      message.error('기존 엑셀 다운로드에 실패했습니다.');
    } finally {
      setLegacyExcelDownloading(false);
    }
  }, [accounts, dispatch, me, selected.length, selectedSlots]);
  
  const handleExcelDownload = useCallback(async () => {
    if (!me) return;
    setExcelDownloading(true);
    try {
      if (selected.length > 0 && selectedSlots.length === 0) {
        message.warning('다운로드할 슬롯이 없습니다.');
        return;
      }

      const { data: job } = await api.post('/slots/excel/export-jobs/',
        selected.length > 0
          ? { ids: selectedSlots.map((slot) => slot.slotId) }
          : { active: false },
      );
      message.loading({ content: `상품명 추출 엑셀을 생성 중입니다. (${job.slot_count}개)`, key: 'product-excel' });

      let jobStatus = job.status;
      while (jobStatus === 'pending' || jobStatus === 'running') {
        await new Promise((resolve) => window.setTimeout(resolve, 1500));
        const { data } = await api.get(`/slots/excel/export-jobs/${job.job_id}/`);
        jobStatus = data.status;
        if (jobStatus === 'failed') {
          throw new Error(data.error || '상품명 엑셀 생성에 실패했습니다.');
        }
      }
      if (jobStatus !== 'complete') {
        throw new Error('상품명 엑셀 생성 상태를 확인할 수 없습니다.');
      }

      const response = await api.get(`/slots/excel/export-jobs/${job.job_id}/download/`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'slots_product_export.xlsx';
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      message.success({
        key: 'product-excel',
        content:
        selected.length > 0
          ? `${selectedSlots.length}개 슬롯이 다운로드되었습니다.`
          : '전체 비활성 슬롯 엑셀 다운로드가 완료되었습니다.',
      });
    } catch (error) {
      message.error({
        key: 'product-excel',
        content: error instanceof Error ? error.message : '엑셀 다운로드에 실패했습니다.',
      });
    } finally {
      setExcelDownloading(false);
    }
  }, [me, selected.length, selectedSlots]);

  /* ==============  에러 알림 ============== */
  useEffect(() => { if (error) message.error(error); }, [error]);

  /* ==============  아직 사용자 정보가 없으면 스피너 ============== */
  if (!me) {
    return <Spin style={{ display: 'block', margin: '160px auto' }} />;
  }

  /* ==============  JSX  ============== */
  return (
    <div ref={topRef}>
      <Card title="환불-종료 슬롯 관리" bodyStyle={{ padding: 0 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div style={{ padding: '0 16px' }}>
            <SlotOpsOverrideBanner />
          </div>
          <ManagementBar
            /* 선택 & 액션 */
            selected={selected}
            selectedSlots={selectedSlots}
            searchField={searchField}
            onSearchFieldChange={handleSearchFieldChange}
            onDelete={onDelete}
            /* 검색 / 상태 */
            search={search}
            onSearchChange={setSearch}
            onSearch={handleSearch}
            status=""
            onStatusChange={() => {}}
            /* 엑셀 */
            onLegacyExcelDownload={handleLegacyExcelDownload}
            onExcelDownload={handleExcelDownload}
            excelUploading={false}
            legacyExcelDownloading={legacyExcelDownloading}
            excelDownloading={excelDownloading}
          />

          <SlotTable
            data={slots}
            loading={loading}
            selected={selected}
            onSelectChange={setSelected}
            onInlineUpdate={async () => {}} // 비활성 페이지에서는 인라인 수정 불가
            pagination={pagination || undefined}
            onPageChange={handlePageChange}
          />
        </Space>
      </Card>
    </div>
  );
};

export default SlotsInactivePage; 
